import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Eye, Inbox, Plus } from 'lucide-react';
import { api, type TicketState } from '../lib/api.js';
import { ago, STATE_LABEL } from '../lib/format.js';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Panel,
  Skeleton,
  StateBadge,
} from '../ui/index.js';

interface Row {
  id: string;
  tenant_id: string;
  number: number;
  state: TicketState;
  subject: string;
  request_type: string | null;
  created_at: string;
  updated_at: string;
  as_subject: boolean;
  queue_name: string | null;
}

/**
 * My requests across ALL groups in one view (FR-PR-03). A requester should
 * never need to know which team owns their request — and tickets raised
 * *about* them appear here too, read-only (FR-PR-06).
 */
export function MyRequests() {
  const q = useQuery({
    queryKey: ['my-tickets'],
    queryFn: () => api.get<{ tickets: Row[] }>('/api/v1/my/tickets'),
  });

  const rows = q.data?.tickets ?? [];
  const open = rows.filter((r) => !['closed', 'cancelled', 'resolved'].includes(r.state));
  const done = rows.filter((r) => ['closed', 'cancelled', 'resolved'].includes(r.state));

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="My requests"
        description="Everything you have asked for, across all six groups"
        actions={
          <Link to="/new">
            <Button variant="primary" leading={<Plus className="h-3.5 w-3.5" />}>
              New request
            </Button>
          </Link>
        }
      />

      {q.isLoading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-line bg-surface p-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-2.5 h-3.5 w-2/3" />
              <Skeleton className="mt-2 h-2.5 w-40" />
            </div>
          ))}
        </div>
      )}

      {q.error && <ErrorState error={q.error} />}

      {q.data && rows.length === 0 && (
        <Panel flush>
          <EmptyState
            icon={<Inbox />}
            title="No requests yet"
            hint="Anything you submit — to any group — appears here, along with anything raised about you."
            action={
              <Link to="/new">
                <Button variant="primary" size="sm">
                  Raise your first request
                </Button>
              </Link>
            }
          />
        </Panel>
      )}

      {open.length > 0 && (
        <Section title="In progress" count={open.length}>
          {open.map((t) => (
            <RequestRow key={t.id} t={t} />
          ))}
        </Section>
      )}

      {done.length > 0 && (
        <Section title="Closed" count={done.length}>
          {done.map((t) => (
            <RequestRow key={t.id} t={t} />
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
        {title}
        <span className="nums rounded-xs bg-surface-sunken px-1 text-3xs">{count}</span>
      </h2>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

function RequestRow({ t }: { t: Row }) {
  return (
    <li>
      <Link
        to={`/t/${t.tenant_id}/${t.number}`}
        className="group flex items-center gap-3 rounded-lg border border-line bg-surface p-3 shadow-xs transition-[background-color,box-shadow] hover:bg-surface-hover hover:shadow-sm"
      >
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <span className="nums text-2xs text-ink-tertiary">#{t.number}</span>
            <StateBadge state={t.state} />
            {t.as_subject && (
              <Badge tone="info" icon={<Eye />}>
                raised about you
              </Badge>
            )}
          </div>
          <p className="truncate text-sm font-medium text-ink">{t.subject}</p>
          <p className="mt-0.5 truncate text-2xs text-ink-tertiary">
            {t.queue_name ?? t.tenant_id}
            {t.request_type && ` · ${t.request_type}`}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-2xs text-ink-tertiary">{ago(t.updated_at)}</p>
          <p className="mt-1 text-2xs text-ink-secondary">{STATE_LABEL[t.state]}</p>
        </div>

        <ChevronRight
          aria-hidden
          className="h-4 w-4 shrink-0 text-ink-tertiary transition-transform group-hover:translate-x-0.5"
        />
      </Link>
    </li>
  );
}
